import { useState, type JSX } from 'react';
import { z } from 'zod';
import type { BotScope } from '@cryptology-assist/domain';
import { botPath, knowledgeSchema } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field } from './components';
import type { Copy } from './copy';

const evidenceSchema = z.object({
  facts: z.array(
    z.object({
      id: z.string(),
      source: z.string(),
      content: z.string(),
      revision: z.string(),
    })
  ),
});

export function EvidencePicker({
  scope,
  copy,
  source,
  evidence,
  onChange,
}: {
  scope: BotScope;
  copy: Copy;
  source: string;
  evidence: string;
  onChange: (source: string, evidence: string) => void;
}): JSX.Element {
  const sources = useRemote(botPath(scope, 'knowledge'), knowledgeSchema);
  const [selected, setSelected] = useState<string | null>(null);
  const selectedId =
    selected ??
    sources.data?.sources.find(
      (item) => item.canonical_uri === source && item.status === 'approved'
    )?.id ??
    '';
  const facts = useRemote(
    selectedId ? botPath(scope, `knowledge/evidence/${selectedId}`) : null,
    evidenceSchema
  );
  return (
    <div>
      <Field label={copy.salesEvidenceSource}>
        <select
          value={selectedId}
          onChange={(event) => setSelected(event.target.value)}
        >
          <option value="">{copy.chooseApprovedSource}</option>
          {sources.data?.sources
            .filter((item) => item.status === 'approved')
            .map((item) => (
              <option key={item.id} value={item.id}>
                {item.canonical_uri}
              </option>
            ))}
        </select>
      </Field>
      <ErrorNotice error={sources.error ?? facts.error} copy={copy} />
      {evidence && <blockquote>{evidence}</blockquote>}
      {facts.data && (
        <Field label={copy.chooseSourcePassage}>
          <select
            value=""
            onChange={(event) => {
              const fact = facts.data!.facts.find(
                (item) => item.id === event.target.value
              );
              if (fact) onChange(fact.source, fact.content.slice(0, 600));
            }}
          >
            <option value="">{copy.chooseSourcePassage}</option>
            {facts.data.facts.map((fact) => (
              <option key={fact.id} value={fact.id}>
                {fact.content.slice(0, 600)}
              </option>
            ))}
          </select>
        </Field>
      )}
      {facts.data &&
        evidence &&
        !facts.data.facts.some(
          (fact) => fact.source === source && fact.content.includes(evidence)
        ) && <p role="alert">{copy.sourceEvidenceChanged}</p>}
    </div>
  );
}
